import { usePagename } from "../../Hooks/usePagename";

import { useTranslation } from "react-i18next";

import { useParams, useLocation } from "react-router-dom";

import { useQuery } from "@apollo/client";

import { ORDER } from "../../Queries/Orders";

import { UPDATE_ORDER } from "../../Mutations/Orders";

import useUpdate from "../../Hooks/Mutations/useUpdate";

import { useState, useEffect, useRef } from "react";

import Breadcrumbs from "../../Components/Dashboard/Breadcrumbs";

import ComponentLoader from "../../Components/Dashboard/Loaders/ComponentLoader";

import Customer from "../../Components/Dashboard/Orders/Single/Customer";

import DeliveryInformationSettings from "../../Components/Dashboard/Orders/Single/DeliveryInfoSettings";

import NotesSettings from "../../Components/Dashboard/Orders/Single/NotesSettings";

import OrderSettings from "../../Components/Dashboard/Orders/Single/Aside/OrderSettings";

import DeliverySettings from "../../Components/Dashboard/Orders/Single/Aside/DeliverySettings";

import PaymentSettings from "../../Components/Dashboard/Orders/Single/Aside/PaymentSettings";

import MontageSettings from "../../Components/Dashboard/Orders/Single/Aside/MontageSettings";

import OrderProductModal from "../../Components/Dashboard/Products/Collection/Modals/OrderProduct/Modal";

import OrderProducts from "../../Components/Dashboard/Orders/Single/Products";

import EditAction from "../../Components/Dashboard/Orders/Single/Aside/EditAction";

import ConfirmationAction from "../../Components/Dashboard/Orders/Single/ConfirmationAction";

import CustomerSearch from "../../Components/Dashboard/Orders/Single/Search";

import PDFGeneratorWidget from "../../Components/Dashboard/Orders/Single/Aside/PDFGenerator";

import SendPDF from "../../Components/Dashboard/Orders/Single/Aside/SendPDF";

import OrderHistory from "../../Components/Dashboard/Orders/Single/OrderHistory";

/**
 * 
 * @exports Content
 * 
 * @description Content component.
 * 
 * @param { Object } props - The props object.
 * 
 * @param { Object } props.data - The data object.
 * 
 * @example <Content data={ data } />
 * 
 * @returns { JSX.Element } Content
 * 
 */

export function Content ( { data, refetch } ) { const 

    { t : __ } = useTranslation (),

    /**
     * 
     * @description Utility hooks.
     * 
     * @type { Object }
     *
     * @property { Function } updateMutation - The update mutation function.
     * 
     * @property { Boolean } loading - The loading state.
     * 
     * @property { Boolean } complete - The complete state.
     * 
     * @property { Object } errors - The errors object.
     * 
     * @property { Function } setErrors - The setErrors function.
     * 
     * @example const { updateMutation, loading, complete, errors, setErrors } = useUpdate ( UPDATE_ORDER );
     * 
     * @returns { Object } updateMutation, loading, complete, errors, setErrors
     * 
     */

    { updateMutation, loading, complete, errors, setErrors } = useUpdate ( UPDATE_ORDER ),

    /** @type { Object } state - The state object. */

    [ state, setState ] = useState ( 
        
        {

            id: data.id || "",

            orderType: data.orderType || "",

            status: data.status || "",

            paymentMethod: data.paymentMethod || "",

            orderPaymentNotes: data.orderPaymentNotes || "",

            vatIncluded: (data.vatIncluded != null) ? data.vatIncluded : true,

            hideTotalsInPdf: !!data.hideTotalsInPdf,

            assembly: data.assembly || false,

            assemblyPrice: data.assemblyPrice || 0, 

            orderDeliveryNotes: data.orderDeliveryNotes || "",

            deliveryPrice: data.deliveryPrice ?? 0,

            deliveryPlanId: data.deliveryPlanId || "",

            firstName: data.firstName || "",

            lastName: data.lastName || "",

            mobilePhoneNr: data.mobilePhoneNr || "",

            email: data.email || "",

            customerIdCode: data.customerIdCode || "",

            companyName: data.companyName || "",

            regNr: data.regNr || "",

            pvnRegNr: data.pvnRegNr || "",

            bank: data.bank || "",

            bankSwift: data.bankSwift || "",

            bankIban: data.bankIban || "",

            adminNotes: data.adminNotes || "",

            userNotes: data.userNotes || "",

            countryCode: data.countryCode || "LV",

            city: data.city || "",

            address: data.address || "",

            postCode: data.postCode || "",

            shippingNotes: data.shippingNotes || "",

            orderProductVendors: data.orderProductVendors || [],

            customDeliveryPrice: data.deliveryPrice ?? 0,

            userId: data.userId || "",

            availableDeliveryMethods: data.availableDeliveryMethods || [],

            deliveryConfirmedAt: data.deliveryConfirmedAt || null,

            trackingIds: data.trackingIds || [],

            price: data.price ?? 0,

            discountPrice: data.discountPrice ?? 0,

            deliveryPriceManuallyEdited: false

        } 
    
    ),

    /** Always read latest form state on submit (avoids stale closure vs. delivery field updates). */
    stateRef = useRef ( null ),

    _syncOrderStateRef = ( stateRef.current = state, 0 ),

    lastServerUpdatedAtRef = useRef ( null ),

    /**
     * After refetch (e.g. save), align totals with the server. Skip the first run so the
     * products calculator can apply live totals without being overwritten by stale props.
     */

    // eslint-disable-next-line no-unused-vars -- registers useEffect inside const chain
    __orderPriceRefetchSync = ( useEffect ( () => {

        if ( data.updatedAt == null ) return;

        if ( lastServerUpdatedAtRef.current === null ) {

            lastServerUpdatedAtRef.current = data.updatedAt;

            return;

        }

        if ( lastServerUpdatedAtRef.current === data.updatedAt ) return;

        lastServerUpdatedAtRef.current = data.updatedAt;

        setState ( s => ( {
            ...s,
            price: data.price ?? 0,
            discountPrice: data.discountPrice ?? 0,
            deliveryPrice: data.deliveryPrice ?? s.deliveryPrice,
            customDeliveryPrice: data.deliveryPrice ?? s.customDeliveryPrice,
            deliveryPriceManuallyEdited: false
        } ) );

    }, [ data.updatedAt, data.price, data.discountPrice, data.deliveryPrice ] ), 0 ),

    displayOrder = { ...data, ...state },

    /**
     * 
     * @event submit
     * 
     * @description Submit form.
     * 
     * @param { Event } e
     * 
     * @returns { void }
     * 
     * @example <form onSubmit={ submit } />
     * 
     */

    submit = e => { e.preventDefault ();

        const s = stateRef.current;

        /** If required fields are not filled, set errors to true. */

        if ( ! s.firstName ) { return setErrors ( { message: "Missing first name" } ); }

        if ( ! s.lastName ) { return setErrors ( { message: "Missing last name" } ); }

        if ( ! s.email ) { return setErrors ( { message: "Missing email" } ); }

        if ( ! s.mobilePhoneNr ) { return setErrors ( { message: "Missing mobile phone number" } ); }

        if ( ! s.orderType ) { return setErrors ( { message: "Missing order type" } ); }

        if ( ! s.status ) { return setErrors ( { message: "Missing status" } ); }

        if ( ! s.userId ) { return setErrors ( { message: "Missing user ID" } ); }

        if ( ! s.deliveryPlanId ) { return setErrors ( { message: "Missing delivery plan" } ); }

        /** If delivery plan requires courier delivery, check if all required fields are filled. */
        const selectedDeliveryMethod = s.availableDeliveryMethods?.find(
            method => parseInt(method.deliveryPlanId) === parseInt(s.deliveryPlanId)
        );

        if ( selectedDeliveryMethod?.preciseAdressRequired ) {
            if ( ! s.city ) { 
                return setErrors ( { message: "Please enter city" } ); 
            }

            if ( ! s.address ) { 
                return setErrors ( { message: "Missing delivery address" } ); 
            }

            if ( ! s.postCode ) { 
                return setErrors ( { message: "Missing postal code" } ); 
            }
        }
        
        updateMutation(productInput(s)).then(() => {

            refetch();
            
        });
    
    };

    /** @returns { JSX.Element } Content */

    return (

        <form onSubmit={ submit }>

            <div className="aside-edit">

                <div className="content">

                    <CustomerSearch state={ state } update={ setState } />

                    <Customer state={ state } update={ setState } />

                    <DeliveryInformationSettings state={ state } update={ setState } />

                    <OrderProductModal orderData={ { view: state, set: setState } } />

                    <OrderProducts order={ state } setState={ setState } isEditing orderId={ data.id } />

                    <NotesSettings state={ state } update={ setState } />

                    <ConfirmationAction errors={ errors } loading={ loading } complete={ complete } />

                    <OrderHistory order={ data } />

                </div>

                <aside>

                    <EditAction errors={ errors } loading={ loading } complete={ complete } order={ displayOrder } />

                    <OrderSettings state={ state } update={ setState } />

                    <DeliverySettings state={ state } update={ setState } />

                    <PDFGeneratorWidget state={ state } order={ displayOrder } />

                    <SendPDF state={ state } order={ displayOrder } />

                    <PaymentSettings state={ state } update={ setState } />

                    <MontageSettings state={ state } update={ setState } />

                    <ConfirmationAction errors={ errors } loading={ loading } complete={ complete } />

                </aside>

            </div>

        </form>

    );

};

/**
 * 
 * @exports EditOrder
 * 
 * @description EditOrder component.
 * 
 * @param { Object } props - The props object.
 * 
 * @param { String } props.name - The name of the page.
 * 
 * @example <EditOrder name="Edit Order" />
 * 
 * @returns { JSX.Element } EditOrder
 * 
 */

export default function EditOrder ( { name } ) { usePagename ( name ); const 

    { orderId } = useParams (),

    location = useLocation (),

    { data, loading, refetch } = useQuery ( ORDER, { variables: { id : orderId } } );

    /** @returns { JSX.Element } EditOrder */

    return (

        <section className="mutations create_order">

            <Breadcrumbs />

            { loading ? <ComponentLoader /> : <Content 
                data={ { 
                    ...data.adminOrder, 
                    // Use availableDeliveryMethods from navigation state if available (from order creation)
                    availableDeliveryMethods: location.state?.availableDeliveryMethods || data.adminOrder?.availableDeliveryMethods || []
                } } 
                refetch={ refetch } 
            /> }

        </section>

    );

};

/**
 * 
 * @function productInput
 * 
 * @description Product input.
 * 
 * @param { Object } state - The state object.
 * 
 * @returns { Object } mutationObject
 * 
 * @example const mutationObject = productInput ( state );
 * 
 */

export function productInput ( state ) { const

    /** @todo add productInput */

    mutationObject = { ...state, [ "productInput" ]: state.orderProductVendors.map (

        item => ( {

            productId: parseInt ( item.productId ),

            discount: parseFloat ( item.discount ),

            price: parseFloat ( item.price ),

            count: parseInt ( item.count )

        } )

    ) };

    /** @todo remove orderProductVendors */

    delete mutationObject.orderProductVendors;

    /** Ensure delivery price sent to mutation matches the admin UI (customDeliveryPrice 0 must not hide a non-zero deliveryPrice). */

    const effectiveDeliveryPrice = ( () => {

        if ( state.deliveryPriceManuallyEdited ) {

            const fromInput = state.deliveryPrice != null && state.deliveryPrice !== ""
                ? parseFloat ( state.deliveryPrice ) : NaN;

            if ( ! Number.isNaN ( fromInput ) ) return fromInput;

            const fromCustom = state.customDeliveryPrice != null && state.customDeliveryPrice !== ""
                ? parseFloat ( state.customDeliveryPrice ) : NaN;

            return Number.isNaN ( fromCustom ) ? 0 : fromCustom;

        }

        if ( state.deliveryPrice != null && state.deliveryPrice !== "" ) {

            const v = parseFloat ( state.deliveryPrice );

            if ( ! Number.isNaN ( v ) ) return v;

        }

        if ( state.customDeliveryPrice != null && state.customDeliveryPrice !== "" ) {

            const v = parseFloat ( state.customDeliveryPrice );

            if ( ! Number.isNaN ( v ) ) return v;

        }

        return 0;

    } ) ();

    mutationObject.deliveryPrice = effectiveDeliveryPrice;

    /** @returns mutation object */

    return mutationObject;

};